import { clearFamilyMembersSeed } from 'app/src/msw/membership/initialSeed/familyMembers'
import { clearMembershipSeed } from 'app/src/msw/membership/initialSeed/membership'
import { clearUserInfoSeed } from 'app/src/msw/membership/initialSeed/userInfo'
import { clearWelfareCampaignSeed } from 'app/src/msw/membership/initialSeed/welfareCampign'
import { clearTransfersSeed } from 'app/src/msw/moneytransfer/initialSeed/transfers'
import { clearRefundsSeed } from 'app/src/msw/refund/initialSeed/refund'
import { creditCollection, getStaticUsedCreditByMembershipId, updateCreditSeed } from 'app/src/msw/shopping/initialSeed/credit'
import { clearOrdersSeed } from 'app/src/msw/shopping/initialSeed/orders'
import { euroFormatter } from 'app/src/shared/helpers'
import { useTranslation } from 'app/src/shared/hooks/useLocalizazion'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { useEffect, useState, type ReactElement } from 'react'

export function AdminPanel(props: { dialogVisible: boolean; setDialogVisible: (visible: boolean) => void }): ReactElement {
  const [art51Credit, setArt51Credit] = useState<number>(1000)
  const [art100Credit, setArt100Credit] = useState<number>(1000)
  const [art51minCredit, setArt51minCredit] = useState<number>(0)
  const [art100minCredit, setArt100minCredit] = useState<number>(0)
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [onlyDiscount, setOnlyDiscount] = useState<boolean>(false)
  const [onlyCatalog, setOnlyCatalog] = useState<boolean>(false)
  const [onlyPlus, setOnlyPlus] = useState<boolean>(false)
  const [hideDemoLabel, setHideDemoLabel] = useState<boolean>(false)

  const { t } = useTranslation('demoApp')

  const clearAllDataAndRedirect = async () => {
    // Pulisci tutti i seed esistenti
    await clearOrdersSeed()
    await clearRefundsSeed()
    await clearTransfersSeed()
    await clearUserInfoSeed()
    await clearWelfareCampaignSeed()
    await clearMembershipSeed()
    await clearFamilyMembersSeed()

    // Costruisci i query params
    const params = new URLSearchParams()
    if (firstName) params.set('firstName', firstName)
    if (lastName) params.set('lastName', lastName)
    if (email) params.set('email', email)
    if (onlyDiscount) params.set('onlyDiscount', 'true')
    if (onlyCatalog) params.set('onlyCatalog', 'true')
    if (onlyPlus) params.set('onlyPlus', 'true')
    if (hideDemoLabel) params.set('hideDemoLabel', 'true')

    // Redirect alla home con i parametri - riuserà la logica esistente in WebAppContextByServiceWorker
    const queryString = params.toString()
    window.location.href = queryString ? `/?${queryString}` : '/'
  }

  const confirmData = async () => {
    // Aggiorna il credito
    await updateCreditSeed(art51Credit, art100Credit)
    // Pulisci e ricarica (hideDemoLabel è già nell'URL)
    await clearAllDataAndRedirect()
  }

  const resetData = async () => {
    await creditCollection.clear()
    await clearAllDataAndRedirect()
  }

  const fetchCreditRange = async (activeMembership: string) => {
    const response = await getStaticUsedCreditByMembershipId(activeMembership)
    setArt51minCredit(response.art51Used)
    setArt100minCredit(response.art100Used)
  }

  useEffect(() => {
    if (props.dialogVisible) {
      const activeMembership = JSON.parse(sessionStorage.getItem('selectedCampaign') || '{}')
      fetchCreditRange(activeMembership?.membershipId)
      // Legge hideDemoLabel dall'URL
      const searchParams = window.location.search
      const hashParams = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : ''
      const urlParams = new URLSearchParams(searchParams || hashParams)
      const hideLabelFromUrl = urlParams.get('hideDemoLabel') === 'true'
      setHideDemoLabel(hideLabelFromUrl)
    }
  }, [props.dialogVisible])

  return (
    <div>
      <Dialog
        onHide={() => {
          props.setDialogVisible(false)
        }}
        visible={props.dialogVisible}
        style={{ width: '90vw', maxWidth: '800px' }}
        contentStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        header={t('AdminPanelTitle')}
      >
        <div>
          <div className='text-md mb-5 text-red-500'>{t('AdminPanelDescription')}</div>

          <div>
            <h4>{t('UserInfoLabel')}</h4>

            <div className='mb-4 mt-4 flex flex-col gap-4'>
              <div className='mb-4 grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div className='flex flex-col gap-2'>
                  <label htmlFor='admin-first-name'>{t('FirstNameLabel')}</label>
                  <InputText
                    id='admin-first-name'
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder='Nome'
                  />
                </div>

                <div className='flex flex-col gap-2'>
                  <label htmlFor='admin-last-name'>{t('LastNameLabel')}</label>
                  <InputText
                    id='admin-last-name'
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder='Cognome'
                  />
                </div>

                <div className='flex flex-col gap-2 md:col-span-2'>
                  <label htmlFor='admin-email'>{t('EmailLabel')}</label>
                  <InputText id='admin-email' value={email} onChange={(e) => setEmail(e.target.value)} placeholder='Email' />
                </div>
              </div>
              <div className='flex flex-col gap-3'>
                <label className='font-semibold'>{t('LayoutConfigurationLabel')}</label>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    inputId='admin-hide-demo-label'
                    checked={hideDemoLabel}
                    onChange={(e) => {
                      const newValue = e.checked || false
                      setHideDemoLabel(newValue)
                      // Aggiorna l'URL parameter
                      const currentUrl = new URL(window.location.href)
                      if (newValue) {
                        currentUrl.searchParams.set('hideDemoLabel', 'true')
                      } else {
                        currentUrl.searchParams.delete('hideDemoLabel')
                      }
                      window.history.replaceState({}, '', currentUrl.toString())
                    }}
                  />
                  <label htmlFor='admin-hide-demo-label' className='cursor-pointer'>
                    {t('HideDemoLabel')}
                  </label>
                </div>
              </div>
              <div className='mt-4 flex flex-col gap-3'>
                <label className='font-semibold'>{t('CampaignConfigurationLabel')}</label>
                <div className='flex items-center gap-2'>
                  <Checkbox inputId='admin-only-plus' checked={onlyPlus} onChange={(e) => setOnlyPlus(e.checked || false)} />
                  <label htmlFor='admin-only-plus' className='cursor-pointer'>
                    Welfare Plus (Catalogo + Sconti)
                  </label>
                </div>

                <div className='flex items-center gap-2'>
                  <Checkbox
                    inputId='admin-only-catalog'
                    checked={onlyCatalog}
                    onChange={(e) => setOnlyCatalog(e.checked || false)}
                  />
                  <label htmlFor='admin-only-catalog' className='cursor-pointer'>
                    Welfare Catalogo
                  </label>
                </div>

                <div className='flex items-center gap-2'>
                  <Checkbox
                    inputId='admin-only-discount'
                    checked={onlyDiscount}
                    onChange={(e) => setOnlyDiscount(e.checked || false)}
                  />
                  <label htmlFor='admin-only-discount' className='cursor-pointer'>
                    Welfare Sconti
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div>
            <h4 className='mt-8'>{t('CreditLabel')}</h4>
            <div className='mb-8 mt-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8'>
              <div className='flex flex-col gap-2'>
                <p>{t('FringeCreditLabel')}</p>
                <InputNumber
                  id='admin-fringe-credit'
                  value={art51Credit}
                  min={0}
                  onBlur={() => setArt51Credit(Math.max(art51minCredit, art51Credit))}
                  onChange={(event) => setArt51Credit(Number(event.value))}
                ></InputNumber>
                <small id='admin-fringe-credit-help' className='text-red-500'>
                  min. {euroFormatter.format(art51minCredit)}
                </small>
              </div>
              <div className='flex flex-col gap-2'>
                <p>{t('NoFringeCreditLabel')}</p>
                <InputNumber
                  id='admin-no-fringe-credit'
                  value={art100Credit}
                  min={0}
                  onBlur={() => setArt100Credit(Math.max(art100minCredit, art100Credit))}
                  onChange={(event) => setArt100Credit(Number(event.value))}
                ></InputNumber>
                <small id='admin-no-fringe-credit-help' className='text-red-500'>
                  min. {euroFormatter.format(art100minCredit)}
                </small>
              </div>
            </div>
          </div>
          <div className='mt-4 flex gap-4'>
            <Button
              className='w-[250px]'
              onClick={() => {
                confirmData()
              }}
              label={t('ConfirmButtonLabel')}
              rounded
              disabled={art51Credit < art51minCredit || art100Credit < art100minCredit}
            ></Button>
          </div>
          <div>
            <h4 className='mt-16'>{t('ResetDataLabel')}</h4>
            <div className='mb-4 mt-4 flex flex-col gap-4'>
              <p>{t('ResetDataText')}</p>
              <Button
                className='w-[250px]'
                onClick={() => {
                  resetData()
                }}
                label={t('ResetDataButtonLabel')}
                rounded
              ></Button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
